// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — statistics on a pin's series (research tier, §Next S1, S3, S6).
 * Turns a `MeasurementSeries` into points with real times, a slope into
 * units a reader expects ("per decade" for a century of projections,
 * "per day" for a month of real-time data), and the tests of `stats.ts`
 * into verdicts that do not claim more than they can:
 *
 * - **A trend is "rising" or "falling" only when least squares
 *   (autocorrelation-corrected, Nychka n_eff) and Mann–Kendall (Hamed–Rao)
 *   both find it at 5 % and agree on its sign.** Mann–Kendall alone is
 *   liberal on persistent series (the 2026-10-06 review measured 16–23 %
 *   false trends); requiring both keeps the rate at least squares' ≈ 5 %.
 * - Each result carries its cautions: few independent values, many tied
 *   slopes (8-bit data), a daily or seasonal cycle (values less than about
 *   a year apart: the tests assume none), a window not fully played.
 * - Time of emergence only on annual (or coarser) values over at least 30
 *   years, with a ten-year running mean, the noise floored at one
 *   quantisation step, and noise from elsewhere when the reader has it.
 * - AYNI (2026-10-10): a series keeps a slot per frame, thousands on a long
 *   stream, and Mann–Kendall costs the square of its points. The tests run
 *   on at most {@link STATS_MAX_SLOTS} time steps, each the mean of the
 *   frames read in it (`thinSeries`); a window with fewer frames is used
 *   frame by frame, as before. Emergence is linear and reads every frame.
 *
 * Pure.
 */

import { thinSeries, type MeasurementSeries } from '../measure/series'
import {
  lagCorrelations,
  mannKendall,
  olsTrend,
  pearson,
  spearman,
  timeOfEmergence,
  type Correlation,
  type Emergence,
  type MannKendall,
  type OlsTrend,
} from './stats'

/** AYNI: time steps the statistics run on at most; more frames are averaged into them. */
export const STATS_MAX_SLOTS = 256

/** AYNI: the series as the statistics see it: at most {@link STATS_MAX_SLOTS} time steps. */
export function statsSeries(series: MeasurementSeries): MeasurementSeries {
  return thinSeries(series, STATS_MAX_SLOTS)
}

/** AYNI: the time between two of the statistics' steps (a correlation's lag is counted in them). */
export function statsStepMs(series: MeasurementSeries): number {
  const steps = Math.min(series.slots.length, STATS_MAX_SLOTS) - 1
  return steps > 0 ? (series.endMs - series.startMs) / steps : series.endMs - series.startMs
}

/** Fewer points than this and no test is reported (the slope alone would invite over-reading). */
export const MIN_TREND_POINTS = 10

const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000
/** The Julian year, the unit climate trends are quoted in. */
const MS_PER_YEAR = 365.25 * MS_PER_DAY
/** Values closer than this have a seasonal or daily cycle the tests do not model. */
const ANNUAL_STEP_MS = 300 * MS_PER_DAY

export type TrendUnit = 'decade' | 'year' | 'day' | 'hour'

export const TREND_UNIT_MS: Readonly<Record<TrendUnit, number>> = {
  decade: 10 * MS_PER_YEAR,
  year: MS_PER_YEAR,
  day: MS_PER_DAY,
  hour: MS_PER_HOUR,
}

/** The unit a span of time is read in: a decade for 20 years or more, a year for 2, a day for 2 days, else an hour. */
export function trendUnitFor(spanMs: number): TrendUnit {
  if (spanMs >= 20 * MS_PER_YEAR) return 'decade'
  if (spanMs >= 2 * MS_PER_YEAR) return 'year'
  if (spanMs >= 2 * MS_PER_DAY) return 'day'
  return 'hour'
}

export interface SeriesPoints {
  /** Epoch ms of each point (the slot's place in the window). */
  readonly times: number[]
  readonly values: number[]
  /** `adjacent[i]`: point i+1 is the very next slot (no gap between them). */
  readonly adjacent: boolean[]
  /** Slot index of each point: its position on the regular time grid. */
  readonly slots: number[]
  /** The time between two slots. */
  readonly stepMs: number
}

/** The series' recorded values with their times; unread and no-data slots left out. */
export function seriesPoints(series: MeasurementSeries): SeriesPoints {
  const last = series.slots.length - 1
  const span = series.endMs - series.startMs
  const times: number[] = []
  const values: number[] = []
  const slots: number[] = []
  series.slots.forEach((slot, i) => {
    if (!slot.sampled || slot.value === null || !Number.isFinite(slot.value)) return
    times.push(series.startMs + (last > 0 ? (span * i) / last : 0))
    values.push(slot.value)
    slots.push(i)
  })
  const adjacent = slots.slice(0, -1).map((s, k) => slots[k + 1] === s + 1)
  return { times, values, adjacent, slots, stepMs: last > 0 ? span / last : span }
}

export interface PinTrend {
  readonly n: number
  readonly unit: TrendUnit
  /** Least squares, per unit, with its autocorrelation-corrected interval and test. */
  readonly ols: OlsTrend
  /** Mann–Kendall with Sen's slope, per unit. */
  readonly mk: MannKendall
  /** Sen's line in series time, for drawing: value at the window's start and end. */
  readonly line: { readonly startMs: number; readonly endMs: number; readonly startValue: number; readonly endValue: number }
  /** Rising or falling only when both tests find it at 5 % and agree on the sign. */
  readonly verdict: 'up' | 'down' | 'none' | 'disagree'
  readonly cautions: {
    /** Less than a quarter of the window read: the result describes what has played. */
    readonly partial: boolean
    /** Fewer than ten independent values. */
    readonly lowIndependent: boolean
    /** More than 30 % of pairwise slopes are exactly zero (ties): Sen's slope is pulled towards 0. */
    readonly manyTies: boolean
    /** Values less than about a year apart: a seasonal or daily cycle the tests assume away. */
    readonly cycle: boolean
  }
}

/** The trend of a dated series, or null with fewer than {@link MIN_TREND_POINTS} values. */
export function pinTrend(full: MeasurementSeries): PinTrend | null {
  const series = statsSeries(full)
  const p = seriesPoints(series)
  if (p.times.length < MIN_TREND_POINTS) return null
  // Fit in the reading unit, so the numbers stay well scaled.
  const unit = trendUnitFor(series.endMs - series.startMs)
  const scale = TREND_UNIT_MS[unit]
  const x = p.times.map((t) => (t - series.startMs) / scale)
  const ols = olsTrend(x, p.values, p.adjacent)
  const mk = mannKendall(x, p.values, 0.95, p.slots)
  if (!ols || !mk) return null
  const xEnd = (series.endMs - series.startMs) / scale
  const olsSig = ols.pValue < 0.05
  const mkSig = mk.pValue < 0.05
  const sameSign = Math.sign(ols.slope) === Math.sign(mk.sen) && mk.sen !== 0
  const verdict: PinTrend['verdict'] = olsSig && mkSig && sameSign
    ? (ols.slope > 0 ? 'up' : 'down')
    : olsSig !== mkSig || (olsSig && mkSig && !sameSign) ? 'disagree' : 'none'
  return {
    n: p.times.length,
    unit,
    ols,
    mk,
    line: {
      startMs: series.startMs,
      endMs: series.endMs,
      startValue: mk.senIntercept,
      endValue: mk.senIntercept + mk.sen * xEnd,
    },
    verdict,
    cautions: {
      partial: p.times.length < series.slots.length / 4,
      lowIndependent: ols.lowIndependent,
      manyTies: mk.zeroSlopeShare > 0.3,
      cycle: p.stepMs < ANNUAL_STEP_MS,
    },
  }
}

/** "p < 0.001", "p = 0.03", "p = 0.42". */
export function formatP(p: number): string {
  if (!Number.isFinite(p)) return 'p = n/a'
  if (p < 0.001) return 'p < 0.001'
  return `p = ${p < 0.01 ? p.toFixed(3) : p.toFixed(2)}`
}

// ------------------------------------------------------------------ emergence (§Next S6)

/** A baseline period for emergence: the window's first 30 years, or its first third when shorter than 90 years. */
export function defaultEmergencePeriod(series: MeasurementSeries): { startMs: number; endMs: number } {
  const span = series.endMs - series.startMs
  const thirty = 30 * MS_PER_YEAR
  return { startMs: series.startMs, endMs: series.startMs + (span >= 3 * thirty ? thirty : span / 3) }
}

export type EmergenceRefusal = 'short' | 'subAnnual' | 'baseline'

export interface PinEmergence {
  readonly emergence: Emergence
  readonly periodStartMs: number
  readonly periodEndMs: number
  /** The running mean's span, in years. */
  readonly windowYears: number
}

export interface EmergenceSettings {
  readonly period?: { startMs: number; endMs: number }
  /** Noise from observations or one model run, in the series' units; null: the baseline's own. */
  readonly noise?: number | null
  /** One quantisation step of the data. */
  readonly quantum?: number | null
  readonly threshold?: number
}

/**
 * Time of emergence for annual (or coarser) values over at least 30 years,
 * with a ten-year centred running mean. A refusal names why: too short,
 * values with a seasonal or daily cycle, or too few baseline values.
 */
export function pinEmergence(series: MeasurementSeries, settings: EmergenceSettings = {}): PinEmergence | EmergenceRefusal {
  if (series.endMs - series.startMs < 30 * MS_PER_YEAR) return 'short'
  // AYNI: frame by frame (it is linear in its points): averaging frames
  // would shrink the baseline's noise and bring emergence forward.
  const p = seriesPoints(series)
  if (p.stepMs < ANNUAL_STEP_MS) return 'subAnnual'
  const period = settings.period ?? defaultEmergencePeriod(series)
  const windowSpan = 10 * MS_PER_YEAR
  const emergence = timeOfEmergence(p.times, p.values, period.startMs, period.endMs, {
    windowSpan,
    step: p.stepMs,
    threshold: settings.threshold ?? 2,
    noise: settings.noise ?? null,
    quantum: settings.quantum ?? null,
  })
  if (!emergence) return 'baseline'
  return { emergence, periodStartMs: period.startMs, periodEndMs: period.endMs, windowYears: 10 }
}

/**
 * Whether a dataset's own description says it is an ensemble statistic (a
 * multi-model mean or median), whose year-to-year variability is far
 * smaller than any single climate's: emergence measured against its own
 * noise comes too early.
 */
export function looksLikeEnsemble(text: string | null | undefined): boolean {
  if (!text) return false
  return /\b(ensemble|multi[- ]model|(median|mean|average) of \d+ (\w+ )?models?|model (median|mean))\b/i.test(text)
}

// ------------------------------------------------------------------ correlation (§Next S3)

export interface PinCorrelation {
  readonly pearson: Correlation
  readonly spearman: Correlation
  /** On the residuals after removing each series' least-squares line: do they vary together, beyond both trending? */
  readonly detrended: Correlation | null
  /** The lag (in steps) where |r| of the detrended series is largest, its p-value corrected for the lags tried (Bonferroni). */
  readonly bestLag: { readonly lag: number; readonly r: number; readonly pCorrected: number; readonly tried: number } | null
  readonly n: number
  /** Values less than about a year apart: shared seasonal or daily cycles inflate r. */
  readonly cycle: boolean
}

/** Two series on the same window, compared slot by slot; null with fewer than ten shared values. */
export function pinCorrelation(fullA: MeasurementSeries, fullB: MeasurementSeries): PinCorrelation | null {
  if (fullA.slots.length !== fullB.slots.length) return null
  const a = statsSeries(fullA)
  const b = statsSeries(fullB)
  const xa: number[] = []
  const xb: number[] = []
  const slots: number[] = []
  const last = a.slots.length - 1
  for (let i = 0; i <= last; i++) {
    const va = a.slots[i]
    const vb = b.slots[i]
    if (!va.sampled || !vb.sampled || va.value === null || vb.value === null) continue
    xa.push(va.value)
    xb.push(vb.value)
    slots.push(i)
  }
  if (xa.length < MIN_TREND_POINTS) return null
  const adjacent = slots.slice(0, -1).map((s, k) => slots[k + 1] === s + 1)
  const p = pearson(xa, xb, adjacent)
  const s = spearman(xa, xb, adjacent)
  if (!p || !s) return null
  const residual = (y: number[]) => {
    const fit = olsTrend(slots, y, adjacent)
    return fit ? y.map((v, i) => v - (fit.intercept + fit.slope * slots[i])) : null
  }
  const ra = residual(xa)
  const rb = residual(xb)
  const detrended = ra && rb ? pearson(ra, rb, adjacent) : null
  let bestLag: PinCorrelation['bestLag'] = null
  if (ra && rb) {
    // Residuals back on the slot grid, so a lag is a step in time.
    const gridA: (number | null)[] = Array(last + 1).fill(null)
    const gridB: (number | null)[] = Array(last + 1).fill(null)
    slots.forEach((slot, k) => {
      gridA[slot] = ra[k]
      gridB[slot] = rb[k]
    })
    const maxLag = Math.max(1, Math.min(10, Math.floor(xa.length / 5)))
    const lags = lagCorrelations(gridA, gridB, maxLag).filter((l) => !l.result.tooFewIndependent)
    if (lags.length) {
      const best = lags.reduce((m, l) => (Math.abs(l.result.r) > Math.abs(m.result.r) ? l : m))
      bestLag = { lag: best.lag, r: best.result.r, pCorrected: Math.min(1, best.result.pValue * lags.length), tried: lags.length }
    }
  }
  const stepMs = last > 0 ? (a.endMs - a.startMs) / last : 0
  return { pearson: p, spearman: s, detrended, bestLag, n: xa.length, cycle: stepMs < ANNUAL_STEP_MS }
}
